import { getPostComments } from "@/api/posts";
import Iconify from "@/components/ui/Iconify";
import Span from "@/components/ui/Span";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { structureComments } from "@/utils/helpers";
import { enqueueSnackbar } from "notistack";
import { forwardRef, useEffect, useState } from "react";
import PostCommentList from "../content-comment-list";
import ContentPlaceCard from "../content-place-card";
import Image from "next/image";
import { useTranslations } from "@/lib/static-intl";
import { useTheme } from "next-themes";
import { useGlobalContext } from "@/context/global/GlobalContext";

const ContentCommentSection = forwardRef(
  (
    {
      selectedPostInModal,
      setTotalComments,
      setCurrentCommentInReply,
      scrollToCommentSection,
      triggerReportModal,
      triggerFullScreenImageSlider,
      openProfile,
      currentUserFollowing,
      setCurrentUserFollowing,
      activeVideo,
      isReelsComment = false,
    },
    ref
  ) => {
    const apiT = useTranslations("api");
    const explorePostDetailsT = useTranslations("explore.explore_post_details");
    const { theme } = useTheme();
    const { user } = useAuthContext();
    const { commentsLoader, setCommentsLoader } = useGlobalContext();

    const [totalPostComments, setTotalPostComments] = useState(0);
    const [allPostComments, setAllPostComments] = useState([]);
    const [commentLikeClick, setCommentLikeClick] = useState({});

    const [pinnedCommentId, setPinnedCommentId] = useState(
      selectedPostInModal?.pinnedCommentId
    );

    const fetchPostComments = async (postId) => {
      setCommentsLoader(true);
      try {
        const response = await getPostComments(postId);

        if (response && Array.isArray(response)) {
          setTotalPostComments(response.length);
          const structuredComments = structureComments(response);

          setAllPostComments(structuredComments);
          setTotalComments(structuredComments?.length);

          const initialCommentLikeClick = {};
          structuredComments.forEach((comment) => {
            if (
              comment.commentLike.some((like) => like.userId === user?.userId)
            ) {
              initialCommentLikeClick[comment.id] = true;
            }
            if (comment.subComments.length > 0) {
              comment.subComments.forEach((subComment) => {
                if (
                  subComment.commentLike.some(
                    (like) => like.userId === user?.userId
                  )
                ) {
                  initialCommentLikeClick[subComment.id] = true;
                }
              });
            }
          });
          setCommentLikeClick(initialCommentLikeClick);
        }
      } catch (error) {
        enqueueSnackbar(
          error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
          {
            variant: "error",
          }
        );
      } finally {
        setCommentsLoader(false);
      }
    };

    useEffect(() => {
      if (selectedPostInModal) {
        fetchPostComments(selectedPostInModal?.id);
      }
    }, [activeVideo, selectedPostInModal?.comments]);

    useEffect(() => {
      if (!isReelsComment) return;
      const container = ref.current;
      if (!container) return;

      let startY = 0;

      const handleTouchStart = (e) => {
        startY = e.touches[0].clientY;
      };

      const handleTouchMove = (e) => {
        if (container.scrollTop === 0 && e.touches[0].clientY > startY) {
          e.preventDefault();
        }
      };

      container.addEventListener("touchstart", handleTouchStart, { passive: true });
      container.addEventListener("touchmove", handleTouchMove, { passive: false });

      return () => {
        container.removeEventListener("touchstart", handleTouchStart);
        container.removeEventListener("touchmove", handleTouchMove);
      };
    }, [isReelsComment, ref]);

    return (
      <div className={`py-5 px-4 ${isReelsComment ? "overflow-y-scroll h-[calc(100%-140px)]" : "h-full"}`} ref={ref}>
        {commentsLoader ? (
          <div className="flex flex-col gap-2 items-center justify-center">
            <Iconify icon={"line-md:loading-loop"} />
            <Span>{explorePostDetailsT("loading")}</Span>
          </div>
        ) : (
          <div className={`w-full flex flex-col gap-2 ${isReelsComment ? "pb-0" : "pb-20"}`} >
            <Span className="!text-sm dark:!text-dark_tertiary_label">
              {explorePostDetailsT("total")} {totalPostComments}{" "}
              {explorePostDetailsT("comments")}
            </Span>
            {allPostComments?.length === 0 ? (
              /*
               * 댓글이 없을 때 이 자리는 늘 비어 있었다(모달 오른쪽 약 480px).
               * 그 자리에 「이 가게」 카드를 놓아 영상 → 가게로 가는 길을 만든다.
               * 릴스는 화면이 좁고 이미 가게 칩이 있어 예전 그대로 둔다.
               * (2026-08-11 데스크톱 검수 ②, 대표님 확정 「나」)
               */
              isReelsComment ? (
                <div className="w-full flex flex-col items-center py-5 opacity-40">
                  <Image
                    src={
                      theme === "light"
                        ? "/collect-empty-dark.png"
                        : "/collect-empty-light.png"
                    }
                    alt="no-comment"
                    width={40}
                    height={40}
                  />
                  <Span className="text-xs">
                    {explorePostDetailsT("no_cmt_added_yet")}
                  </Span>
                </div>
              ) : (
                <div className="w-full flex flex-col gap-5 pt-1">
                  <ContentPlaceCard place={selectedPostInModal?.place} />
                  <Span className="!text-xs dark:!text-dark_tertiary_label">
                    {explorePostDetailsT("no_cmt_added_yet")}
                  </Span>
                </div>
              )
            ) : (
              <PostCommentList
                openProfile={openProfile}
                pinnedCommentId={pinnedCommentId}
                allPostComments={allPostComments}
                commentLikeClick={commentLikeClick}
                setTotalComments={setTotalComments}
                setPinnedCommentId={setPinnedCommentId}
                setAllPostComments={setAllPostComments}
                selectedPostInModal={selectedPostInModal}
                scrollToCommentSection={scrollToCommentSection}
                setCurrentCommentInReply={setCurrentCommentInReply}
                triggerReportModal={triggerReportModal}
                triggerFullScreenImageSlider={triggerFullScreenImageSlider}
                currentUserFollowing={currentUserFollowing}
                setCurrentUserFollowing={setCurrentUserFollowing}
              />
            )}
          </div>
        )}
      </div>
    );
  }
);

export default ContentCommentSection;
